"use client";

import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import {
  Building2Icon,
  BadgeDollarSignIcon,
  LineChartIcon,
} from "lucide-react";
import { Fragment } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCards, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-cards";

// ==================== TYPES ====================
interface User {
  first_name: string;
  last_name: string;
  profile_picture_url?: string | null;
}

interface ProfileAttribute {
  key: string;
  title: string;
  value: string;
}

interface ProfileCompany {
  name: string;
  logo_url?: string | null;
  role: string | null;
  location: string | null;
  since_date: string | null;
  description: string | null;
  size?: string;
  valuation?: string;
  growth?: string;
}

export interface ProfileDetail {
  user: User;
  headline: string;
  company: ProfileCompany;
  attributes: ProfileAttribute[];
}

interface CompanyOverviewHighlightData {
  icon: React.ReactNode;
  title: string;
  value: string;
}

interface CompanyOverviewData {
  name: string;
  imageUrl: string | null | undefined;
  role: string | null;
  location: string | null;
  sinceDate: string | null;
  description: string | null;
  highlights: CompanyOverviewHighlightData[];
}

// ==================== UTILITIES ====================
function getUserFullName(user: User): string {
  return `${user.first_name} ${user.last_name}`.trim();
}

function getUserInitials(user: User): string {
  const firstName = user.first_name?.[0] || "";
  const lastName = user.last_name?.[0] || "";
  return (firstName + lastName).toUpperCase();
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function getCompanyData(data: ProfileCompany): CompanyOverviewData {
  return {
    name: data.name,
    imageUrl: data.logo_url,
    description: data.description,
    sinceDate: data.since_date,
    location: data.location,
    role: data.role,
    highlights: [
      ...(data.size
        ? [
            {
              icon: <Building2Icon />,
              title: "Company Size",
              value: data.size,
            },
          ]
        : []),
      ...(data.valuation
        ? [
            {
              icon: <BadgeDollarSignIcon />,
              title: "Est. Valuation",
              value: data.valuation,
            },
          ]
        : []),
      ...(data.growth
        ? [
            {
              icon: <LineChartIcon />,
              title: "Est. Growth",
              value: data.growth,
            },
          ]
        : []),
    ],
  };
}

// ==================== UI COMPONENTS ====================

// Avatar Component
interface AvatarProps {
  imageUrl?: string | null;
  alt: string;
  initials: string;
  size: "12" | "15" | "24";
  roundedClassName?: string;
}

function Avatar({
  imageUrl,
  alt,
  initials,
  size,
  roundedClassName = "rounded-full",
}: AvatarProps) {
  const sizeClasses = {
    "12": "size-12",
    "15": "size-15",
    "24": "size-24",
  };

  return (
    <div
      className={clsx(
        "flex items-center justify-center bg-surface-secondary text-text-secondary font-semibold shrink-0",
        sizeClasses[size],
        roundedClassName,
      )}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={alt}
          className={clsx("w-full h-full object-cover", roundedClassName)}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
}

// Button Component
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: "small" | "medium" | "large";
  variant?: "border" | "solid" | "ghost";
  icon?: React.ReactNode;
}

function Button({
  size = "medium",
  variant = "solid",
  icon,
  children,
  className,
  ...props
}: ButtonProps) {
  const sizeClasses = {
    small: "px-2 py-1 text-sm",
    medium: "px-4 py-2 text-base",
    large: "px-6 py-3 text-lg",
  };

  const variantClasses = {
    border:
      "border border-stroke-default bg-transparent hover:bg-surface-secondary",
    solid: "bg-primary text-white hover:bg-primary-dark",
    ghost: "bg-transparent hover:bg-surface-secondary",
  };

  return (
    <button
      className={clsx(
        "flex items-center gap-2 rounded-lg transition-colors",
        sizeClasses[size],
        variantClasses[variant],
        className,
      )}
      {...props}
    >
      {icon && <span className="size-4">{icon}</span>}
      {children}
    </button>
  );
}

// Markdown Component (simplified)
function Markdown({
  children,
  className,
}: {
  children: string;
  className?: string;
}) {
  return (
    <div
      className={clsx("prose", className)}
      dangerouslySetInnerHTML={{ __html: children }}
    />
  );
}

// Separator Component
function Separator({ className }: { className?: string }) {
  return <hr className={clsx("border-t border-stroke-default", className)} />;
}

// ==================== PROFILE COMPONENTS ====================

// ProfilePersonaHeader Component
interface ProfilePersonaHeaderProps {
  compact?: boolean;
  headline: string;
  data: User;
}

function ProfilePersonaHeader({
  compact,
  data,
  headline,
}: ProfilePersonaHeaderProps) {
  return (
    <div
      className={clsx("relative flex items-center gap-2", {
        "flex-col": !compact,
      })}
    >
      <Avatar
        imageUrl={data.profile_picture_url}
        alt={getUserFullName(data)}
        initials={getUserInitials(data)}
        size={compact ? "12" : "24"}
      />
      <div
        className={clsx("flex flex-col gap-1", {
          "items-center": !compact,
        })}
      >
        <h2
          className={clsx("text-text-secondary font-semibold", {
            "text-xl text-center": !compact,
            "text-lg": compact,
          })}
        >
          {getUserFullName(data)}
        </h2>
        <p
          className={clsx("text-text-alternative font-normal", {
            "text-lg text-center": !compact,
            "text-sm": compact,
          })}
        >
          {headline}
        </p>
      </div>
    </div>
  );
}

// CompanyOverview Component
const colorClassNames = [
  "[--accent-color:var(--color-accent-red)] [--accent-surface-color:var(--color-accent-red-surface)]",
  "[--accent-color:var(--color-accent-green)] [--accent-surface-color:var(--color-accent-green-surface)]",
  "[--accent-color:var(--color-accent-blue)] [--accent-surface-color:var(--color-accent-blue-surface)]",
];

function CompanyOverview({
  data: { name, imageUrl, role, highlights, description, location, sinceDate },
}: {
  data: CompanyOverviewData;
}) {
  return (
    <div className="flex flex-col gap-4 p-4 border border-stroke-default bg-shape-default-blur rounded-3xl">
      <div className="flex gap-3 items-start">
        <Avatar
          imageUrl={imageUrl}
          alt={name}
          initials={name && getInitials(name)}
          size="15"
          roundedClassName="rounded-2xl"
        />
        <div className="flex flex-col grow min-w-0 gap-1 justify-center overflow-hidden">
          <div className="text-base text-text-primary font-medium truncate">
            {role}
          </div>
          <div className="flex flex-col overflow-hidden">
            <div className="text-sm text-text-alternative font-normal whitespace-nowrap truncate">
              {name}
              {location ? `, ${location}` : ""}
            </div>
            <div className="text-sm text-text-alternative font-normal truncate">
              {sinceDate}
            </div>
          </div>
        </div>
      </div>
      {highlights.length > 0 && (
        <div className="grid grid-cols-6 gap-3">
          {highlights
            .map((item, index) => ({
              id: index,
              ...item,
              colorClassName: colorClassNames[index],
            }))
            .map((item, index) => (
              <div
                key={item.id}
                className={clsx(
                  "relative col-span-2 flex flex-col items-center gap-2 py-4 rounded-2xl bg-linear-to-t from-(--accent-surface-color) to-transparent",
                  "ring-1 ring-inset ring-stroke-default",
                  item.colorClassName,
                  {
                    "col-start-3": highlights.length === 1 && index === 0,
                    "col-start-2": highlights.length === 2 && index === 0,
                  },
                )}
              >
                <Slot className="size-6 shrink-0 text-shape-light">
                  {item.icon}
                </Slot>
                <div className="flex flex-col items-center gap-1">
                  <div className="text-base font-semibold text-text-alternative text-center">
                    {item.value}
                  </div>
                  <div className="text-xxs font-normal text-text-alternative text-center">
                    {item.title}
                  </div>
                </div>
                <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-6 h-1 rounded-t-sm bg-(--accent-color)" />
              </div>
            ))}
        </div>
      )}
      {description && (
        <Markdown className="prose-base text-text-alternative">
          {description}
        </Markdown>
      )}
    </div>
  );
}

// ProfileAttributes Component
interface ProfileAttributesProps {
  attributes: ProfileAttribute[];
}

function ProfileAttributes({ attributes }: ProfileAttributesProps) {
  return (
    <div className="flex flex-col gap-4 p-4 border border-stroke-default bg-shape-default-blur rounded-3xl">
      {attributes.map(
        (attribute, index) =>
          attribute.value && (
            <Fragment key={attribute.key}>
              {index !== 0 && <Separator />}
              <div className="flex gap-3 items-start">
                <div className="flex grow flex-col gap-2">
                  <div className="text-xs font-normal text-text-alternative">
                    {attribute.title}
                  </div>
                  <Markdown className="text-base font-normal text-text-alternative">
                    {attribute.value}
                  </Markdown>
                </div>
              </div>
            </Fragment>
          ),
      )}
    </div>
  );
}

// ==================== SINGLE PROFILE CARD ====================
type ProfilePersonaDetailProps = {
  compact?: boolean;
  className?: string;
  profileDetail: ProfileDetail;
};

function ProfilePersonaDetail({
  profileDetail,
  compact,
  className,
}: ProfilePersonaDetailProps) {
  return (
    <div className={clsx("flex grow flex-col gap-4 p-0 ", className)}>
      <ProfilePersonaHeader
        data={profileDetail.user}
        headline={profileDetail.headline}
        compact={compact}
      />
      <CompanyOverview data={getCompanyData(profileDetail.company)} />
      <ProfileAttributes attributes={profileDetail.attributes} />
    </div>
  );
}

// ==================== CONTAINER COMPONENT ====================
interface ProfilePersonaContainerProps {
  profiles: ProfileDetail[];
  className?: string;
}

export default function ProfilePersonaContainer({
  profiles,
  className,
}: ProfilePersonaContainerProps) {
  return (
    <div
      className={clsx(
        "w-full h-120 max-h-120 lg:h-[50vh] flex items-center justify-center",
        className,
      )}
    >
      <Swiper
        effect="cards"
        grabCursor={true}
        modules={[EffectCards, Autoplay]}
        className="w-[18rem] lg:w-100 h-[95%]"
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
        }}
        loop={true}
      >
        {profiles.map((profile, index) => (
          <SwiperSlide
            key={index}
            className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-300 shadow-2xl"
          >
            <div className="w-full h-full p-4 overflow-y-hidden">
              <ProfilePersonaDetail profileDetail={profile} />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
